import React, { useEffect, useState } from "react";
import useSenders from "../../hooks/useSenders";
import SendersTable from "../../components/Tables/SendersTable/SendersTable";
import GenericFormModal from "../../components/Modals/GenericFormModal/GenericFormModal";
import SenderForm from "../../components/Forms/SenderForm/SenderForm";
import ActionConfirmationModal from "../../components/Modals/ActionConfirmationModal/ActionConfirmationModal";
import { Button } from "antd";
import DecisionConfirmationModal from "../../components/Modals/DecisionConfirmationModal/DecisionConfirmationModal";
import NotAllowed from "../../components/NotAllowed/NotAllowed";
import { useDispatch, useSelector } from "react-redux";
import { userProfiles } from "../../redux/slices/UserProfilesSlice";
import { handleRoleNotAllowedRedirection } from "../../utils/userRolesUtils";
import SendersSearch from "../../components/SendersSearch/SendersSearch";
import {
  selectSendersSearch,
  setValue,
  commitCriteria,
  setPagination,
} from "../../redux/slices/sendersSearchSlice";

const formModalConfig = {
  width: "80%",
};

const confirmationModalContent = {
  successAdded: {
    type: "success",
    title: "Registro añadido con éxito.",
  },
  successModified: {
    type: "success",
    title: "Registro modificado con éxito.",
  },
  successRemoved: {
    type: "success",
    title: "Registro eliminado con éxito.",
  },
  errorAdded: {
    type: "error",
    title: "Error al añadir remitente.",
    text: "Error al añadir remitente. Por favor, inténtelo de nuevo más tarde.",
  },
  errorRemoved: {
    type: "error",
    title: "Error al eliminar remitente.",
    text: "Error al eliminar remitente. Por favor, inténtelo de nuevo más tarde.",
  },
  errorModified: {
    type: "error",
    title: "Error al modificar remitente.",
    text: "Error al modificar remitente. Por favor, inténtelo de nuevo más tarde.",
  },
};

const decisionModalContent = {
  deleteSender: {
    title: "Eliminar remitente",
    confirmButtonText: "Eliminar",
    cancelButtonText: "Cancelar",
    text: "¿Desea eliminar al remitente ",
  },
};

export default function Senders(props) {
  let modalContent;
  const dispatch = useDispatch();
  const userRoles = useSelector(userProfiles);
  const isUserAllowed = handleRoleNotAllowedRedirection(userRoles);

  const searchParameters = useSelector(selectSendersSearch);

  const {
    getPagedSenders,
    addSender,
    updateSender,
    sendersDetails,
    setSendersDetails,
    deleteSender,
  } = useSenders();

  const [genericModalDetails, setGenericModalDetails] = useState({
    isVisible: false,
    content: "senderCreateForm",
  });

  const [confirmationModalDetails, setConfirmationModalDetails] = useState({
    isVisible: false,
    type: "",
    errorMessage: "",
  });

  const [
    decisionConfirmationModalDetails,
    setDecisionConfirmationModalDetails,
  ] = useState({
    isVisible: false,
    type: "",
  });

  const [activeSender, setActiveSender] = useState({});

  const fetchSenders = async () => {
    await getPagedSenders();
  };

  useEffect(() => {
    if (!isUserAllowed) return;

    const hasCriteria = Boolean(searchParameters.participantName);
    if (searchParameters.searchWasMade && hasCriteria) {
      getPagedSenders(searchParameters.page, searchParameters.size, searchParameters.participantName, false);
    } else if (!searchParameters.searchWasMade) {
      getPagedSenders(1, searchParameters.size, null, false);
    }
  }, [isUserAllowed]);

  const handleSearchInputChange = (e) => {
    dispatch(setValue(e?.target?.value ?? ""));
  };

  const handleSearchSenders = async (event) => {
    event.preventDefault();
    const value = (searchParameters.value || "").trim();
    const nextParticipantName = value || null;

    dispatch(commitCriteria({ participantName: nextParticipantName }));
    await getPagedSenders(1, searchParameters.size, nextParticipantName, false);
  };

  const fetchWithCurrentCriteria = async ({ afterDelete = false } = {}) => {
    let nextPage = searchParameters.page;

    if (afterDelete) {
      const wasLastItem = sendersDetails?.data?.length === 1 && searchParameters.page > 1;
      if (wasLastItem) nextPage = searchParameters.page - 1;
    }

    await getPagedSenders(nextPage, searchParameters.size, searchParameters.participantName, false);

    if (nextPage !== searchParameters.page) {
      dispatch(setPagination({ page: nextPage }));
    }
  };

  const handleEditSender = async (senderDetails) => {
    try {
      handleToggleGenericModal();
      const updatedSender = await updateSender(senderDetails);

      if (updatedSender?.apierror) {
        return handleToggleConfirmationModal(
          "errorModified",
          updatedSender.apierror.message
        );
      }

      if (updatedSender) {
        await fetchWithCurrentCriteria();
        return handleToggleConfirmationModal("successModified");
      }
      handleToggleConfirmationModal("errorModified");
    } catch (error) {
      console.error(error);
      handleToggleConfirmationModal("errorModified");
    }
  };

  const handleDeleteSender = async () => {
    try {
      handleToggleDecisionConfirmationModal();
      const deletedSender = await deleteSender(activeSender.participantId);

      if (deletedSender?.apierror) {
        return handleToggleConfirmationModal(
          "errorRemoved",
          deletedSender.apierror.message
        );
      }

      if (deletedSender) {
        await fetchWithCurrentCriteria({ afterDelete: true });
        return handleToggleConfirmationModal("successRemoved");
      }

      handleToggleConfirmationModal("errorRemoved");
    } catch (error) {
      console.error(error);
      handleToggleDecisionConfirmationModal("errorRemoved");
    }
  };

  const handleAddSender = async (senderDetails) => {
    try {
      handleToggleGenericModal();
      const addedSender = await addSender(senderDetails);
      if (addedSender.status === "ok") {
        return handleToggleConfirmationModal("successAdded");
      }

      handleToggleConfirmationModal("errorAdded");
    } catch (error) {
      console.error(error);
      handleToggleConfirmationModal("errorAdded");
    }
  };

  const handleToggleGenericModal = (content, activeSenderDetails) => {
    if (!content) {
      return setGenericModalDetails((prevState) => ({
        ...prevState,
        isVisible: !prevState.isVisible,
      }));
    }

    if (activeSenderDetails) {
      setActiveSender(activeSenderDetails);
    }

    setGenericModalDetails((prevState) => ({
      content: content,
      isVisible: !prevState?.isVisible,
    }));
  };

  const handleSaveForm = () => {
    handleToggleGenericModal();
  };

  const handleToggleConfirmationModal = (type, apierror) => {
    if (!type) {
      return setConfirmationModalDetails((prevState) => ({
        ...prevState,
        isVisible: !prevState.isVisible,
        errorMessage: "",
      }));
    }

    setConfirmationModalDetails((prevState) => ({
      ...prevState,
      type: type,
      isVisible: !prevState.isVisible,
      errorMessage: apierror,
    }));
  };

  const handleToggleDecisionConfirmationModal = (activeSenderDetails) => {
    if (!activeSenderDetails) {
      return setDecisionConfirmationModalDetails((prevState) => ({
        ...prevState,
        isVisible: !prevState.isVisible,
      }));
    }
    setActiveSender(activeSenderDetails);
    return setDecisionConfirmationModalDetails((prevState) => ({
      ...prevState,
      isVisible: !prevState.isVisible,
      type: "deleteSender",
    }));
  };

  const actions = {
    addSender: handleAddSender,
    editSender: handleEditSender,
    deleteSender: handleDeleteSender,
    setGenericModalDetails: setGenericModalDetails,
    toggleGenericModal: handleToggleGenericModal,
    toggleDecisionConfirmationModal: handleToggleDecisionConfirmationModal,
    setActiveSender: setActiveSender,
    save: handleSaveForm,
    getPagedSenders: getPagedSenders,
    setSendersDetails: setSendersDetails,
  };

  if (genericModalDetails?.content === "senderEditForm") {
    modalContent = (
      <SenderForm
        activeSender={activeSender}
        actions={actions}
        formType="senderEditForm"
        callback={handleEditSender}
      />
    );
  }

  if (genericModalDetails?.content === "senderCreateForm") {
    modalContent = (
      <SenderForm
        actions={actions}
        formType="senderCreateForm"
        callback={handleAddSender}
      />
    );
  }

  if (!isUserAllowed) {
    return (
      <NotAllowed
        isUserAllowed={isUserAllowed}
        isLoading={userRoles?.isLoading}
      />
    );
  }

  return (
    <div className="Senders">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 25,
          alignItems: "center",
        }}
      >
        <h2>Remitentes</h2>

        <Button
          onClick={() => actions.toggleGenericModal("senderCreateForm")}
          className="add-btn"
        >
          Añadir remitente
        </Button>
      </div>

      <SendersSearch
        searchParameters={searchParameters}
        onValueChange={handleSearchInputChange}
        onSubmit={handleSearchSenders}
      />

      {searchParameters.searchWasMade && (
        <SendersTable
          sendersDetails={sendersDetails}
          tableActions={actions}
        />
      )}
      <GenericFormModal
        title={genericModalDetails.title}
        isVisible={genericModalDetails?.isVisible}
        actions={actions}
        modalConfig={formModalConfig}
      >
        {modalContent}
      </GenericFormModal>

      <ActionConfirmationModal
        modalDetails={confirmationModalDetails}
        callback={handleToggleConfirmationModal}
        modalContent={confirmationModalContent}
      />

      <DecisionConfirmationModal
        modalDetails={decisionConfirmationModalDetails}
        modalContent={decisionModalContent}
        callback={handleDeleteSender}
        toggleModal={handleToggleDecisionConfirmationModal}
        activeParticipant={activeSender}
      />
    </div>
  );
}
